/**
 * WP8 Part B — TypeScript side of the export contract (`docs/specs/WP8-exports.md`). The CSV
 * endpoints are plain same-origin URLs the UI links/downloads directly (the browser follows the
 * server's `Content-Disposition: attachment`); the chapters/description endpoints are fetched as
 * text so their content can be copied to the clipboard.
 */
import { apiGetText } from './api';
import type { SectionTimestampEntry } from './rooms';
import type { Section, Template } from './templates';

export function roomExportCsvUrl(roomId: number): string {
  return `/api/rooms/${roomId}/export.csv`;
}

export function roomChaptersUrl(roomId: number): string {
  return `/api/rooms/${roomId}/chapters.txt`;
}

export function roomDescriptionUrl(roomId: number): string {
  return `/api/rooms/${roomId}/description.txt`;
}

export function templateExportCsvUrl(templateId: number): string {
  return `/api/templates/${templateId}/export.csv`;
}

export function fetchRoomChapters(roomId: number): Promise<string> {
  return apiGetText(roomChaptersUrl(roomId));
}

export function fetchRoomDescription(roomId: number): Promise<string> {
  return apiGetText(roomDescriptionUrl(roomId));
}

/**
 * Client-side approximation of the backend's chapter count (spec's "Chapters rules"): for each
 * `isChapter` section, the first `section_timestamp` entered at or after `streamStartedAt` counts
 * as one chapter. Used only to drive the `ExportMenu` disabled-reason UI — the export endpoint
 * itself is the authority on the final list (offsets, `0:00` forcing, clamping).
 */
export function estimateChapterCount(
  template: Pick<Template, 'sections'>,
  sectionTimestamps: SectionTimestampEntry[],
  streamStartedAt: string | null,
): number {
  if (streamStartedAt === null) {
    return 0;
  }
  const startMs = new Date(streamStartedAt).getTime();
  const chapterSectionIds = new Set(
    template.sections.filter((section: Section) => section.isChapter).map((section) => section.id),
  );
  const counted = new Set<number>();
  for (const entry of sectionTimestamps) {
    if (!chapterSectionIds.has(entry.sectionId)) {
      continue;
    }
    if (new Date(entry.enteredAt).getTime() < startMs) {
      continue;
    }
    counted.add(entry.sectionId);
  }
  return counted.size;
}
